import { useState } from 'react';

import AddRoundedIcon from '@mui/icons-material/AddRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  IconButton,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import { EmptyState, ErrorState, LoadingState } from '@/shared/ui';

import { useCreateSalesChannel, useSalesChannels, useUpdateSalesChannel } from '../../hooks';
import { useCatalogPermissions } from '../../lib/useCatalogPermissions';
import { useToast } from '../../lib/useToast';
import type { SalesChannelResponse } from '../../model/types';
import { ChannelFormDialog, type ChannelFormValue } from './ChannelFormDialog';

/** Channels & modes panel — list sales channels, create/edit, set default, set mirror. */
export function ChannelsPanel() {
  const { data: channels, isLoading, isError, refetch } = useSalesChannels();
  const create = useCreateSalesChannel();
  const update = useUpdateSalesChannel();
  const { canPricingManage } = useCatalogPermissions();
  const { toast, success, error, clear } = useToast();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<SalesChannelResponse | undefined>();

  if (isLoading) return <LoadingState label="Загрузка каналов…" />;
  if (isError) {
    return (
      <ErrorState
        message="Не удалось загрузить каналы"
        onRetry={() => void refetch()}
      />
    );
  }

  const list = channels ?? [];

  const handleSubmit = async (v: ChannelFormValue) => {
    try {
      if (editing) {
        await update.mutateAsync({
          code: editing.code,
          body: {
            name: v.name,
            active: v.active,
            isDefault: v.isDefault,
            mirrorsChannelCode: v.mirrorsChannelCode || undefined,
          },
        });
      } else {
        await create.mutateAsync({
          code: v.code,
          name: v.name,
          active: v.active,
          isDefault: v.isDefault,
          mirrorsChannelCode: v.mirrorsChannelCode || undefined,
        });
      }
      success('Канал сохранён');
      setDialogOpen(false);
      setEditing(undefined);
    } catch {
      error('Не удалось сохранить канал');
    }
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Каналы продаж и режим наследования цен (зеркалирование = общая цена с другим каналом).
        </Typography>
        {canPricingManage && (
          <Button
            startIcon={<AddRoundedIcon />}
            variant="outlined"
            onClick={() => {
              setEditing(undefined);
              setDialogOpen(true);
            }}
          >
            Добавить канал
          </Button>
        )}
      </Stack>
      {list.length === 0 ? (
        <EmptyState title="Каналов пока нет" description="Добавьте первый канал продаж." />
      ) : (
        <Card variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Канал</TableCell>
                <TableCell>Код</TableCell>
                <TableCell>Режим</TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {list.map((c) => (
                <TableRow key={c.id} hover>
                  <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography fontWeight={600}>{c.name}</Typography>
                      {c.isDefault && (
                        <Chip size="small" label="По умолчанию" variant="outlined" />
                      )}
                      {!c.active && <Chip size="small" color="default" label="Выключен" />}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <code>{c.code}</code>
                  </TableCell>
                  <TableCell>
                    {c.mirrorsChannelCode ? (
                      <Chip
                        size="small"
                        color="info"
                        label={`зеркалит ${c.mirrorsChannelCode}`}
                      />
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        собственные / базовые цены
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {canPricingManage && (
                      <IconButton
                        size="small"
                        aria-label="edit"
                        onClick={() => {
                          setEditing(c);
                          setDialogOpen(true);
                        }}
                      >
                        <EditRoundedIcon fontSize="small" />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
      <ChannelFormDialog
        open={dialogOpen}
        editing={editing}
        channels={list}
        saving={create.isPending || update.isPending}
        onClose={() => {
          setDialogOpen(false);
          setEditing(undefined);
        }}
        onSubmit={handleSubmit}
      />
      <Snackbar
        open={toast !== null}
        autoHideDuration={5000}
        onClose={clear}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={clear} severity={toast?.severity ?? 'info'} variant="filled" sx={{ width: '100%' }}>
          {toast?.message ?? ''}
        </Alert>
      </Snackbar>
    </Box>
  );
}
